<template>
  <FormModal
    ref="FormModal"
    :title="modalTitle"
    @closed="onClosed">
    <template slot="section">
      <div v-if="record">
        <template v-if="!isEditing">
          <div
            v-for="rule in classificationRules"
            :key="`${rule.level}-${rule.ruleId}`"
            class="procedure-report-justification-modal__rule mb-4">
            <div class="d-flex align-center justify-space-between mb-2">
              <span class="font-weight-bold primary--text">
                {{ levelLabel(rule.level) }}
              </span>
              <Button
                outlined
                small
                @click="startEditing(rule)">
                {{ $t('procedureReport.actions.rowActions.editRule') }}
              </Button>
            </div>

            <div class="procedure-report-justification-modal__rule-field mb-1">
              <span class="font-weight-medium">{{ $t('procedureReport.fields.ruleValue') }}:</span>
              {{ rule.value }}
            </div>
            <div
              v-if="rule.criteria"
              class="procedure-report-justification-modal__rule-field mb-1">
              <span class="font-weight-medium">{{ $t('procedureReport.fields.ruleCriteria') }}:</span>
              {{ rule.criteria }}
            </div>
            <div
              v-if="rule.reason"
              class="procedure-report-justification-modal__rule-field">
              <span class="font-weight-medium">{{ $t('procedureReport.fields.ruleReason') }}:</span>
              {{ rule.reason }}
            </div>
          </div>

          <div
            v-if="!classificationRules.length"
            class="grey--text">
            {{ $t('procedureReport.messages.noResultText') }}
          </div>
        </template>

        <template v-else>
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                v-model="editForm.level"
                :items="levelOptions"
                item-text="name"
                item-value="id"
                :label="$t('procedureReport.fields.ruleLevel')"
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="12" md="6">
              <Textfield
                :value="editForm.value"
                :label="$t('procedureReport.fields.ruleValue')"
                dense
                outlined
                hide-details
                @input="editForm.value = $event"
              />
            </v-col>
          </v-row>

          <div class="d-flex justify-end gap-h-15 mt-4">
            <Button
              outlined
              :disabled="loading"
              @click="cancelEditing">
              {{ $t('common.cancel') }}
            </Button>
            <Button
              :loading="loading"
              :disabled="!canSubmitEdit"
              @click="submitEdit">
              {{ $t('common.save') }}
            </Button>
          </div>
        </template>
      </div>
    </template>
  </FormModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import FormModal from '@/components/Modals/FormModal/FormModal.vue';
import Button from '@/components/Default/Button/Button.vue';
import Textfield from '@/components/Default/Textfield.vue';
import { classificationLevelOptions } from '@/store/modules/procedure-report/procedure-report-config';
import { getClassificationRules } from '@/store/modules/procedure-report/procedure-report-factory';

export default {
  name: 'ProcedureReportJustificationModal',
  components: {
    FormModal,
    Button,
    Textfield,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isEditing: false,
    editForm: {
      level: null,
      value: '',
    },
  }),
  computed: {
    ...mapGetters({ justificationModal: 'procedure-report/justificationModal' }),
    record() {
      return this.justificationModal.record;
    },
    editingRule() {
      return this.justificationModal.editingRule;
    },
    classificationRules() {
      return getClassificationRules(this.record);
    },
    levelOptions() {
      return classificationLevelOptions.map((option) => ({
        id: option.id,
        name: this.$t(option.nameKey),
      }));
    },
    modalTitle() {
      return this.$t('procedureReport.modals.editRuleTitle');
    },
    canSubmitEdit() {
      return Boolean(this.editForm.level)
        && Boolean(String(this.editForm.value || '').trim());
    },
  },
  watch: {
    'justificationModal.show': {
      handler(show) {
        this.$nextTick(() => {
          if (!this.$refs.FormModal) return;

          if (show) {
            this.isEditing = Boolean(this.editingRule);
            if (this.editingRule) {
              this.fillEditForm(this.editingRule);
            } else {
              this.resetEditForm();
            }
            this.$refs.FormModal.open();
            return;
          }

          this.$refs.FormModal.close();
          this.isEditing = false;
          this.resetEditForm();
        });
      },
    },
    'justificationModal.editingRule': {
      handler(editingRule) {
        if (!this.justificationModal.show) return;

        this.isEditing = Boolean(editingRule);
        if (editingRule) {
          this.fillEditForm(editingRule);
        } else {
          this.resetEditForm();
        }
      },
    },
  },
  methods: {
    ...mapActions({
      correctClassificationRule: 'procedure-report/correctClassificationRule',
      closeJustificationModal: 'procedure-report/closeJustificationModal',
      setJustificationEditingRule: 'procedure-report/setJustificationEditingRule',
    }),
    levelLabel(level) {
      const option = classificationLevelOptions.find((item) => item.id === level);
      return option ? this.$t(option.nameKey) : level;
    },
    fillEditForm(rule) {
      this.editForm = {
        level: rule.level,
        value: rule.value,
      };
    },
    resetEditForm() {
      this.editForm = {
        level: null,
        value: '',
      };
    },
    startEditing(rule) {
      this.fillEditForm(rule);
      this.isEditing = true;
      this.setJustificationEditingRule(rule);
    },
    cancelEditing() {
      this.isEditing = false;
      this.resetEditForm();
      this.setJustificationEditingRule(null);
    },
    async submitEdit() {
      if (!this.record || !this.canSubmitEdit) return;

      const result = await this.correctClassificationRule({
        procedureId: this.record.id,
        level: this.editForm.level,
        value: String(this.editForm.value).trim(),
      });

      if (result) {
        this.isEditing = false;
        this.resetEditForm();
      }
    },
    onClosed() {
      if (!this.justificationModal.show) return;
      this.closeJustificationModal();
    },
  },
};
</script>

<style lang="scss" scoped>
.procedure-report-justification-modal__rule {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  padding: 12px 16px;
}

.procedure-report-justification-modal__rule-field {
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
